import { Injectable } from '@angular/core';
import { BaseViewModel } from '../../../models/base/base-view-model';
import { exists } from '../../../functions/exists';
import { combineLatest } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PrintCardDomainModel } from '../../../domain/print-card-domain-model';
import { VariantAssetService } from '../../services/variant-asset-service';

@Injectable()
export abstract class RenderContentViewModel extends BaseViewModel {
  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected printCardDomainModel: PrintCardDomainModel,
    protected variantAssetService: VariantAssetService
  ) {
    super();
  }

  public readonly companyConfig$ = this.printCardDomainModel.companyConfig$;
  public readonly locationId$ = this.printCardDomainModel.locationId$;
  public readonly locationConfig$ = this.printCardDomainModel.locationConfig$;
  public readonly printCardMenu$ = this.printCardDomainModel.printCardMenu$;
  public readonly laysOutCardsOnToPaperMenu$ = this.printCardDomainModel.laysOutCardsOnToPaperMenu$;
  public readonly shelfTalkerMenu$ = this.printCardDomainModel.shelfTalkerMenu$;
  public readonly adobeFontFamilyCssUrl$ = this.printCardDomainModel.adobeFontFamilyCssUrl$;
  public readonly cardStacks$ = this.printCardDomainModel.cardStacks$;
  public readonly shelfTalkerCards$ = this.printCardDomainModel.shelfTalkerCards$;
  public readonly overrideVariantIds$ = this.printCardDomainModel.overrideVariantIds$;
  public readonly overrideSectionIds$ = this.printCardDomainModel.overrideSectionIds$;
  public readonly overrideSiblingVariantIds$ = this.printCardDomainModel.overrideSiblingVariantIds$;
  public readonly variantCardCountMap$ = this.printCardDomainModel.variantCardCountMap$;
  public readonly visibleOverrideVariantIdsWithVisibleSiblingIds$ =
    this.printCardDomainModel.visibleOverrideVariantIdsWithVisibleSiblingIds$;

  /* *************************** Local Threads of Execution *************************** */

  /**
   * Order matters here, set sectionIds before variantIds, as the latter depends on the former.
   */
  private sendUrlParamsToDomainModel = combineLatest([
    this.activatedRoute.url,
    this.activatedRoute.params,
    this.activatedRoute.queryParams
  ]).subscribeWhileAlive({
    owner: this,
    next: ([urlsSegments, params, queryParams]) => {
      const isPreview = urlsSegments?.some(seg => seg.path === 'preview');
      const url = this.router.url;
      this.parseLocationIdIfItExists(params);
      const containsSectionIds = this.parseSectionIdsIfTheyExist(queryParams);
      this.parseSiblingVariantIdsIfTheyExist(queryParams);
      const containsVariantIds = this.parseVariantIdsIfTheyExist(queryParams);
      this.parsePrintCardMenuIdIfItExists(url, params, isPreview, containsVariantIds, containsSectionIds);
      this.parsePrintCardMenuTemplateIdIfItExists(url, params, isPreview, containsVariantIds, containsSectionIds);
      this.parseVariantCardCountMapIfItExists(queryParams);
    }
  });

  private parseLocationIdIfItExists(params: Params): void {
    if (exists(params?.locationId)) {
      const locationId = params?.locationId ? Number.parseInt(params?.locationId, 10) : null;
      this.printCardDomainModel.connectToLocationId(locationId);
    }
  }

  /**
   * When sectionIds are being specified, we can call the get-partial-xxx endpoint.
   */
  private parseSectionIdsIfTheyExist(queryParams: Params): boolean {
    let containsSectionIds = false;
    if (exists(queryParams?.sectionIds)) {
      containsSectionIds = queryParams?.sectionIds?.length > 0;
      const sectionIds = queryParams?.sectionIds?.split(',') ?? [];
      this.printCardDomainModel.connectToOverrideSectionIds(sectionIds);
    } else {
      this.printCardDomainModel.connectToOverrideSectionIds([]);
    }
    return containsSectionIds;
  }

  private parseSiblingVariantIdsIfTheyExist(queryParams: Params): void {
    if (exists(queryParams?.siblingVariantIds)) {
      const siblingVariantIds = queryParams?.siblingVariantIds?.split(',') ?? [];
      this.printCardDomainModel.connectToOverrideSiblingVariantIds(siblingVariantIds);
    } else {
      this.printCardDomainModel.connectToOverrideSiblingVariantIds([]);
    }
  }

  /**
   * When variantIds are being specified, we can call the get-partial-xxx endpoint.
   *
   * Setting the pipeline to an empty array is done on purpose, to signal other pipelines that the url has been
   * parsed, which lets them know that they can start executing.
   */
  private parseVariantIdsIfTheyExist(queryParams: Params): boolean {
    let containsVariantIds = false;
    if (exists(queryParams?.variantIds)) {
      containsVariantIds = queryParams?.variantIds?.length > 0;
      // VariantIds will always be specified for building a card-stack PDF
      const variantIds = queryParams?.variantIds?.split(',') ?? [];
      this.printCardDomainModel.connectToOverrideVariantIds(variantIds);
    } else {
      // Unlike the other pipelines in here, the variant pipeline is more complex because it also needs to handle
      // sibling variant IDs. If override variants are specified, we don't immediately pass them to the
      // VariantAssetService. Instead, we wait for `visibleOverrideVariantIdsWithVisibleSiblingIds$` from the
      // PrintCardDomainModel to emit, ensuring the list includes both override and sibling variant IDs.
      // As a result, `this.printCardDomainModel.connectToOverrideVariantIds` does not set
      // `this.variantAssetService.connectToOverrideVariantIds` directly.
      this.printCardDomainModel.connectToOverrideVariantIds([]);
      this.variantAssetService.connectToOverrideVariantIds([]);
    }
    return containsVariantIds;
  }

  private parsePrintCardMenuIdIfItExists(
    url: string,
    params: Params,
    isPreview: boolean,
    containsVariantIds: boolean,
    containsSectionIds: boolean
  ): void {
    if (exists(params?.printCardMenuId)) {
      if (isPreview || containsVariantIds || containsSectionIds) {
        url?.includes('print-shelf-talker')
          ? this.printCardDomainModel.connectToPartialShelfTalkerMenuId(params.printCardMenuId)
          : this.printCardDomainModel.connectToPartialPrintCardMenuId(params.printCardMenuId);
      } else {
        this.printCardDomainModel.connectToPrintCardMenuId(params.printCardMenuId);
      }
    }
  }

  private parsePrintCardMenuTemplateIdIfItExists(
    url: string,
    params: Params,
    isPreview: boolean,
    containsVariantIds: boolean,
    containsSectionIds: boolean
  ): void {
    if (exists(params?.printCardMenuTemplateId)) {
      if (isPreview || containsVariantIds || containsSectionIds) {
        url?.includes('print-shelf-talker')
          ? this.printCardDomainModel.connectToPartialShelfTalkerTemplateId(params.printCardMenuTemplateId)
          : this.printCardDomainModel.connectToPartialPrintCardMenuTemplateId(params.printCardMenuTemplateId);
      } else {
        this.printCardDomainModel.connectToPrintCardMenuTemplateId(params.printCardMenuTemplateId);
      }
    }
  }

  private parseVariantCardCountMapIfItExists(queryParams: Params): void {
    if (exists(queryParams?.variantCardCountMap)) {
      try {
        const variantCardCountMap = JSON.parse(queryParams?.variantCardCountMap);
        this.printCardDomainModel.connectToVariantCardCountMap(variantCardCountMap);
      } catch (e) {
        console.error('Failed to parse variantCardCountMap', e);
      }
    }
  }

  /* ********************************************************************************** */
}
